import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Stepper, Step, StepLabel, StepContent, LinearProgress, Chip, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import TaskCard from '../components/TaskCard';
import { offlineApiService } from '../services/offlineApi';

const Dashboard: React.FC = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState<any[]>([]);

  useEffect(() => {
    // Check if user needs to complete onboarding
    if (!loading && user && !user.onboarding?.completed) {
      navigate('/onboarding');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    // Only fetch tasks if user is authenticated and onboarding is complete
    if (user && user.onboarding?.completed) {
      fetchTasks();
    }
  }, [user]);

  const fetchTasks = async () => {
    try {
      await offlineApiService.getGardens();
      // Mock tasks for now
      setTasks([
        { _id: '1', title: 'Water your tomatoes', description: 'They need water every 2 days', status: 'pending', xpReward: 10, gardenId: '1' },
        { _id: '2', title: 'Check for pests', description: 'Inspect leaves for any signs of damage', status: 'pending', xpReward: 15, gardenId: '1' },
        { _id: '3', title: 'Harvest ripe vegetables', description: 'Some tomatoes look ready!', status: 'pending', xpReward: 30, gardenId: '1' },
      ]);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleTaskComplete = (taskId: string) => {
    setTasks(tasks.map(task => 
      task._id === taskId ? { ...task, status: 'completed' } : task
    ));
  };

  // Determine user's current stage based on onboarding
  const getUserStage = () => {
    if (!user?.onboarding?.completed) return 0;
    // TODO: Add user farms, gardens, spaces, plants when available
    // For now, we'll use the onboarding step
    const step = user?.onboarding?.step || 0;
    return Math.min(step, 4);
  };

  const roadmapSteps = [
    {
      label: 'Plant Your First Seed',
      description: 'Start your journey by adding your first plant',
      tasks: ['Add a plant', 'Set watering schedule', 'Take a photo'],
      completed: getUserStage() > 0
    },
    {
      label: 'Create Your Garden',
      description: 'Organize your plants into a garden',
      tasks: ['Name your garden', 'Set garden type (indoor/outdoor)', 'Add 3 plants'],
      completed: getUserStage() > 1
    },
    {
      label: 'Design Your Space',
      description: 'Group gardens into logical spaces',
      tasks: ['Create a space', 'Add 2 gardens to space', 'Invite a friend'],
      completed: getUserStage() > 2
    },
    {
      label: 'Establish Your Farm',
      description: 'Build your complete growing ecosystem',
      tasks: ['Name your farm', 'Add location', 'Create harvest schedule'],
      completed: getUserStage() > 3
    },
    {
      label: 'Master Gardener',
      description: 'Achieve expert status',
      tasks: ['Complete 50 tasks', 'Harvest 100 items', 'Reach Gold League'],
      completed: false
    }
  ];

  const activeStep = roadmapSteps.findIndex(step => !step.completed);

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // If no user, they'll be redirected by the ProtectedRoute
  if (!user) {
    return null;
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50', pb: 10 }}>
      {/* Header */}
      <Box sx={{ bgcolor: 'primary.main', color: 'white', pt: 3, pb: 2 }}>
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight="bold" gutterBottom>
            🌱 Farm Development
          </Typography>
          <Typography variant="body2">
            {user?.username}'s Journey
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        {/* Quick Stats */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">🔥 {user?.gamification?.streak || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Streak
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">⭐ {user?.gamification?.xp || 0}</Typography>
                <Typography variant="body2" color="text.secondary">
                  Total XP
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">🌱 0</Typography>
                <Typography variant="body2" color="text.secondary">
                  Plants
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">🏡 0</Typography>
                <Typography variant="body2" color="text.secondary">
                  Gardens
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Today's Tasks */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Today's Tasks
              </Typography>
              <Chip 
                label={`${tasks.filter(t => t.status === 'completed').length}/${tasks.length} completed`}
                color="primary"
                size="small"
              />
            </Box>
            
            {tasks.length > 0 ? (
              tasks.map(task => (
                <TaskCard 
                  key={task._id} 
                  task={task} 
                  onComplete={handleTaskComplete}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No tasks for today. Great job! 🎉
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Farm Development Roadmap */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Your Farm Development Roadmap
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Follow this path to build your complete farming ecosystem
            </Typography>

            <Stepper activeStep={activeStep} orientation="vertical">
              {roadmapSteps.map((step, index) => (
                <Step key={index} completed={step.completed}>
                  <StepLabel
                    StepIconComponent={() => step.completed ? <CheckCircleIcon color="success" /> : <RadioButtonUncheckedIcon color={index === activeStep ? 'primary' : 'disabled'} />}
                  >
                    <Typography variant="subtitle1" fontWeight="bold">
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                      {step.description}
                    </Typography>
                    <List dense>
                      {step.tasks.map((task, taskIndex) => (
                        <ListItem key={taskIndex}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon 
                              sx={{ 
                                fontSize: 20,
                                color: step.completed ? 'success.main' : 'action.disabled'
                              }} 
                            />
                          </ListItemIcon>
                          <ListItemText 
                            primary={task}
                            primaryTypographyProps={{
                              variant: 'body2',
                              sx: { 
                                textDecoration: step.completed ? 'line-through' : 'none',
                                color: step.completed ? 'text.secondary' : 'text.primary'
                              }
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                    {index === activeStep && (
                      <Box mt={2}>
                        <Button 
                          variant="contained" 
                          size="small"
                          onClick={() => {
                            // Navigate based on current step
                            if (index === 0) navigate('/plants/add');
                            else if (index === 1) navigate('/gardens/create');
                            else if (index === 2) navigate('/spaces/create');
                            else if (index === 3) navigate('/farms/create');
                          }}
                        >
                          Get Started
                        </Button>
                      </Box>
                    )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>

            {/* Overall Progress */}
            <Box mt={4}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  Overall Progress
                </Typography>
                <Typography variant="body2" color="primary">
                  {Math.round((roadmapSteps.filter(s => s.completed).length / roadmapSteps.length) * 100)}%
                </Typography>
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={(roadmapSteps.filter(s => s.completed).length / roadmapSteps.length) * 100}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default Dashboard;